/**
 * Mock data for Tekkr Project View (single team)
 * Product Request Process - Frontend Team
 */

export interface MetricData {
  current: number;
  target: number;
  trend: number[]; // 7-day trend
  unit: string;
}

export interface Alert {
  metric: string;
  threshold: number;
  direction: 'below' | 'above';
  owner: string;
  channels: string[];
}

export interface ProjectData {
  projectName: string;
  teamId: string;
  metrics: {
    channelCompliance: MetricData;
    slaAdherence: MetricData;
    timeToFirstResponse: MetricData;
    overdueBacklog: MetricData;
    routingAccuracy: MetricData;
  };
  alerts: Alert[];
}

export const projectMockData: ProjectData = {
  projectName: "Product Request Process - Frontend Team",
  teamId: "eng-frontend",
  metrics: {
    channelCompliance: {
      current: 72,
      target: 85,
      trend: [65, 68, 70, 71, 72, 73, 72],
      unit: "%"
    },
    slaAdherence: {
      current: 92,
      target: 90,
      trend: [88, 89, 90, 91, 92, 91, 92],
      unit: "%"
    },
    timeToFirstResponse: {
      current: 26.2,
      target: 24,
      trend: [25.8, 26.0, 26.1, 26.3, 26.2, 26.4, 26.2],
      unit: "hours"
    },
    overdueBacklog: {
      current: 12,
      target: 5,
      trend: [8, 9, 10, 11, 12, 13, 12],
      unit: "tickets"
    },
    routingAccuracy: {
      current: 88,
      target: 95,
      trend: [90, 89, 88, 87, 88, 88, 88],
      unit: "%"
    }
  },
  alerts: [
    {
      metric: "slaAdherence",
      threshold: 70,
      direction: "below",
      owner: "Jackie Chen",
      channels: ["Slack", "Email"]
    }
  ]
};

/**
 * 4-week historical data for detailed trend chart
 * 28 days of daily data
 */
export interface HistoricalData {
  date: string;
  channelCompliance: number;
  slaAdherence: number;
  timeToFirstResponse: number;
  overdueBacklog: number;
  routingAccuracy: number;
}

export const projectHistoricalData: HistoricalData[] = [
  // Week 1
  { date: "Nov 18", channelCompliance: 65, slaAdherence: 85, timeToFirstResponse: 24.5, overdueBacklog: 6, routingAccuracy: 92 },
  { date: "Nov 19", channelCompliance: 66, slaAdherence: 86, timeToFirstResponse: 24.8, overdueBacklog: 6, routingAccuracy: 91 },
  { date: "Nov 20", channelCompliance: 64, slaAdherence: 87, timeToFirstResponse: 24.2, overdueBacklog: 7, routingAccuracy: 92 },
  { date: "Nov 21", channelCompliance: 67, slaAdherence: 88, timeToFirstResponse: 25.0, overdueBacklog: 7, routingAccuracy: 91 },
  { date: "Nov 22", channelCompliance: 68, slaAdherence: 87, timeToFirstResponse: 25.5, overdueBacklog: 8, routingAccuracy: 90 },
  { date: "Nov 23", channelCompliance: 69, slaAdherence: 88, timeToFirstResponse: 25.8, overdueBacklog: 8, routingAccuracy: 90 },
  { date: "Nov 24", channelCompliance: 68, slaAdherence: 89, timeToFirstResponse: 25.6, overdueBacklog: 8, routingAccuracy: 91 },
  
  // Week 2
  { date: "Nov 25", channelCompliance: 70, slaAdherence: 88, timeToFirstResponse: 26.0, overdueBacklog: 9, routingAccuracy: 89 },
  { date: "Nov 26", channelCompliance: 71, slaAdherence: 89, timeToFirstResponse: 26.2, overdueBacklog: 9, routingAccuracy: 89 },
  { date: "Nov 27", channelCompliance: 69, slaAdherence: 90, timeToFirstResponse: 25.9, overdueBacklog: 10, routingAccuracy: 90 },
  { date: "Nov 28", channelCompliance: 70, slaAdherence: 89, timeToFirstResponse: 26.3, overdueBacklog: 10, routingAccuracy: 88 },
  { date: "Nov 29", channelCompliance: 72, slaAdherence: 90, timeToFirstResponse: 26.5, overdueBacklog: 11, routingAccuracy: 87 },
  { date: "Nov 30", channelCompliance: 71, slaAdherence: 91, timeToFirstResponse: 26.4, overdueBacklog: 11, routingAccuracy: 88 },
  { date: "Dec 1", channelCompliance: 70, slaAdherence: 90, timeToFirstResponse: 26.1, overdueBacklog: 10, routingAccuracy: 89 },
  
  // Week 3
  { date: "Dec 2", channelCompliance: 71, slaAdherence: 91, timeToFirstResponse: 26.3, overdueBacklog: 11, routingAccuracy: 87 },
  { date: "Dec 3", channelCompliance: 72, slaAdherence: 92, timeToFirstResponse: 26.4, overdueBacklog: 12, routingAccuracy: 88 },
  { date: "Dec 4", channelCompliance: 73, slaAdherence: 91, timeToFirstResponse: 26.6, overdueBacklog: 12, routingAccuracy: 87 },
  { date: "Dec 5", channelCompliance: 72, slaAdherence: 92, timeToFirstResponse: 26.3, overdueBacklog: 13, routingAccuracy: 88 },
  { date: "Dec 6", channelCompliance: 71, slaAdherence: 91, timeToFirstResponse: 26.5, overdueBacklog: 13, routingAccuracy: 88 },
  { date: "Dec 7", channelCompliance: 73, slaAdherence: 92, timeToFirstResponse: 26.4, overdueBacklog: 12, routingAccuracy: 87 },
  { date: "Dec 8", channelCompliance: 74, slaAdherence: 93, timeToFirstResponse: 26.2, overdueBacklog: 12, routingAccuracy: 88 },
  
  // Week 4 (most recent)
  { date: "Dec 9", channelCompliance: 73, slaAdherence: 92, timeToFirstResponse: 26.2, overdueBacklog: 12, routingAccuracy: 88 },
  { date: "Dec 10", channelCompliance: 72, slaAdherence: 91, timeToFirstResponse: 26.1, overdueBacklog: 13, routingAccuracy: 88 },
  { date: "Dec 11", channelCompliance: 71, slaAdherence: 92, timeToFirstResponse: 26.3, overdueBacklog: 13, routingAccuracy: 87 },
  { date: "Dec 12", channelCompliance: 72, slaAdherence: 91, timeToFirstResponse: 26.4, overdueBacklog: 12, routingAccuracy: 88 },
  { date: "Dec 13", channelCompliance: 73, slaAdherence: 92, timeToFirstResponse: 26.2, overdueBacklog: 12, routingAccuracy: 88 },
  { date: "Dec 14", channelCompliance: 72, slaAdherence: 92, timeToFirstResponse: 26.3, overdueBacklog: 13, routingAccuracy: 88 },
  { date: "Dec 15", channelCompliance: 72, slaAdherence: 92, timeToFirstResponse: 26.2, overdueBacklog: 12, routingAccuracy: 88 },
];

